export type ParsedContactQr = {
  nome?: string
  telefone?: string
  source: 'vcard' | 'wa_me' | 'whatsapp_api' | 'tel' | 'phone_digits' | 'unknown'
}

function normalizeBrazilPhone(digits: string): string | undefined {
  const raw = digits.replace(/\D/g, '')
  if (raw.length < 8) return undefined

  let normalized = raw
  if (normalized.startsWith('00')) {
    normalized = normalized.slice(2)
  }

  // BR sem DDI: 10–11 dígitos
  if (normalized.length === 10 || normalized.length === 11) {
    normalized = `55${normalized}`
  }

  if (normalized.length < 10 || normalized.length > 15) return undefined
  return normalized
}

function parseVCard(payload: string): ParsedContactQr | null {
  if (!/BEGIN:VCARD/i.test(payload)) return null

  const lines = payload.split(/\r?\n/)
  let nome: string | undefined
  let telefone: string | undefined

  for (const line of lines) {
    const fn = line.match(/^FN(?:;[^:]+)?:(.+)$/i)
    if (fn?.[1]) {
      nome = fn[1].trim()
      continue
    }

    const n = line.match(/^N(?:;[^:]+)?:([^;]+)/i)
    if (!nome && n?.[1]) {
      const parts = n[1].split(';').filter(Boolean)
      nome = parts.reverse().join(' ').trim() || undefined
    }

    const tel = line.match(/^TEL(?:;[^:]+)?:(.+)$/i)
    if (tel?.[1] && !telefone) {
      telefone = normalizeBrazilPhone(tel[1])
    }
  }

  if (!nome && !telefone) return null
  return { nome, telefone, source: 'vcard' }
}

function parseWaMeUrl(payload: string): ParsedContactQr | null {
  const match = payload.match(/wa\.me\/(?:qr\/)?(\d{8,15})/i)
  if (!match?.[1]) return null
  const telefone = normalizeBrazilPhone(match[1])
  if (!telefone) return null
  return { telefone, source: 'wa_me' }
}

function parseWhatsAppApiUrl(payload: string): ParsedContactQr | null {
  try {
    const url = new URL(payload)
    if (!/whatsapp\.com$/i.test(url.hostname) && !/api\.whatsapp\.com$/i.test(url.hostname)) {
      return null
    }
    const phone = url.searchParams.get('phone')
    if (!phone) return null
    const telefone = normalizeBrazilPhone(phone)
    if (!telefone) return null
    return { telefone, source: 'whatsapp_api' }
  } catch {
    return null
  }
}

function parseTelUri(payload: string): ParsedContactQr | null {
  const match = payload.trim().match(/^tel:([+\d\s().-]+)$/i)
  if (!match?.[1]) return null
  const telefone = normalizeBrazilPhone(match[1])
  if (!telefone) return null
  return { telefone, source: 'tel' }
}

function parsePlainPhone(payload: string): ParsedContactQr | null {
  const trimmed = payload.trim()
  if (!/^\+?[\d\s().-]{8,20}$/.test(trimmed)) return null
  const telefone = normalizeBrazilPhone(trimmed)
  if (!telefone) return null
  return { telefone, source: 'phone_digits' }
}

/** Interpreta QR de contato (vCard, wa.me, tel:, etc.) para pré-preencher lead. */
export function parseContactQrPayload(raw: string): ParsedContactQr | null {
  const payload = raw.trim()
  if (!payload) return null

  const parsers = [
    parseVCard,
    parseWaMeUrl,
    parseWhatsAppApiUrl,
    parseTelUri,
    parsePlainPhone,
  ]

  for (const parser of parsers) {
    const parsed = parser(payload)
    if (parsed?.telefone || parsed?.nome) return parsed
  }

  // wa.me pode estar embutido em texto maior
  const embeddedWa = parseWaMeUrl(payload)
  if (embeddedWa) return embeddedWa

  return null
}
